// Wizard de creación de eventos en 3 pasos + "Opciones avanzadas" colapsada.
// - Autoguardado del borrador en el navegador en cada cambio (retoma al volver).
// - Sala de espera virtual encendida por defecto.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft, ArrowRight, Plus, Trash2, ImageIcon, Check, Building2, ChevronDown, Loader2, ShieldCheck, X, Copy,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { createEvent } from "@/lib/events.functions";
import { supabase } from "@/integrations/supabase/client";
import { categories } from "@/lib/mock-data";
import { AccessGate } from "@/components/permissions/AccessGate";
import { usePermissions } from "@/lib/permissions";
import { EventPreviewCard } from "@/components/event/EventPreviewCard";
import { SalesAdvisor } from "@/components/event/SalesAdvisor";
import {
  autoQueueThreshold, defaultWizardState, detectTimezone, totalTickets, validateStep1, validateTickets,
  type StepErrors, type WizardAdvanced, type WizardState, type WizardTicket,
} from "@/lib/event-wizard";

export const Route = createFileRoute("/create-event")({
  component: CreateEventGuarded,
  head: () => ({
    meta: [
      { title: "Crear Evento — ZYNTRA" },
      { name: "description", content: "Publicá tu evento en menos de 2 minutos." },
      { property: "og:title", content: "Crear Evento — ZYNTRA" },
      { property: "og:description", content: "Publicá tu evento en menos de 2 minutos." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function CreateEventGuarded() {
  return (
    <AccessGate required="create-events">
      <CreateEventWizard />
    </AccessGate>
  );
}

const inputCls =
  "mt-1 w-full rounded-xl border border-border bg-secondary px-4 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

function Field({ label, error, htmlFor, hint, children }: { label: string; error?: string; htmlFor: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">{label}</label>
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-xs font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}
    >
      <span className={`absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-background transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function CreateEventWizard() {
  const { user, loading: authLoading } = useAuth();
  const perms = usePermissions();
  const eligibleTenants = useMemo(() => perms.tenantsWithCreateEvents, [perms.tenantsWithCreateEvents]);

  const storageKey = user ? `zyntra:event-draft:${user.id}` : null;
  const [s, setS] = useState<WizardState>(() => defaultWizardState());
  const [hydrated, setHydrated] = useState(false);
  const [errors, setErrors] = useState<StepErrors>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState<null | "publish" | "draft">(null);
  const [submitError, setSubmitError] = useState("");
  const [created, setCreated] = useState<{ id: string; published: boolean } | null>(null);

  // Restaurar borrador guardado (o detectar zona horaria en uno nuevo).
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw) as WizardState;
        const base = defaultWizardState();
        setS({ ...base, ...saved, advanced: { ...base.advanced, ...saved.advanced } });
      } else {
        setS((p) => ({ ...p, timezone: detectTimezone() }));
      }
    } catch {}
    setHydrated(true);
  }, [storageKey]);

  // Autoguardado en cada cambio.
  useEffect(() => {
    if (!hydrated || !storageKey || created) return;
    localStorage.setItem(storageKey, JSON.stringify(s));
  }, [s, hydrated, storageKey, created]);

  const set = <K extends keyof WizardState>(k: K, v: WizardState[K]) => setS((p) => ({ ...p, [k]: v }));
  const setAdv = <K extends keyof WizardAdvanced>(k: K, v: WizardAdvanced[K]) =>
    setS((p) => ({ ...p, advanced: { ...p.advanced, [k]: v } }));
  const setTicket = (id: string, patch: Partial<WizardTicket>) =>
    setS((p) => ({ ...p, tickets: p.tickets.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));

  // Validación en tiempo real del paso actual una vez que hubo un intento.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) return;
    setErrors(s.step === 1 ? validateStep1(s) : s.step === 2 ? validateTickets(s.tickets) : {});
  }, [s, touched]);

  const capacity = parseInt(s.advanced.capacity) || totalTickets(s.tickets);
  const effectiveThreshold = parseInt(s.advanced.waitingRoomThreshold) || autoQueueThreshold(capacity);

  function goNext() {
    const e = s.step === 1 ? validateStep1(s) : validateTickets(s.tickets);
    setTouched(true);
    setErrors(e);
    if (Object.keys(e).length) return;
    setTouched(false);
    set("step", (s.step + 1) as 2 | 3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goBack() {
    setErrors({});
    setTouched(false);
    set("step", (s.step - 1) as 1 | 2);
  }

  async function submit(publish: boolean) {
    setSubmitError("");
    const e1 = validateStep1(s), e2 = validateTickets(s.tickets);
    if (Object.keys(e1).length) { set("step", 1); setErrors(e1); setTouched(true); return; }
    if (Object.keys(e2).length) { set("step", 2); setErrors(e2); setTouched(true); return; }
    if (eligibleTenants.length > 1 && !s.tenantId) { setSubmitError("Elegí la organización del evento"); return; }

    setSubmitting(publish ? "publish" : "draft");
    try {
      const a = s.advanced;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const result = await createEvent({
        data: {
          title: s.title.trim(),
          description: a.description.trim() || undefined,
          date: s.date,
          time: s.startTime,
          endTime: s.endTime || undefined,
          timezone: s.timezone,
          isVirtual: s.isVirtual,
          virtualUrl: s.isVirtual ? s.virtualUrl.trim() : undefined,
          location: s.isVirtual ? "Evento virtual" : s.location.trim(),
          city: s.isVirtual ? "Online" : s.city.trim(),
          category: a.category,
          imageUrl: s.imageUrl || undefined,
          maxPerUser: Math.min(20, Math.max(1, parseInt(a.maxPerUser) || 4)),
          isPublished: publish,
          capacity: parseInt(a.capacity) || undefined,
          waitingRoomEnabled: a.waitingRoomEnabled,
          waitingRoomThreshold: effectiveThreshold,
          salesStartAt: a.salesStart ? new Date(a.salesStart).toISOString() : undefined,
          salesEndAt: a.salesEnd ? new Date(a.salesEnd).toISOString() : undefined,
          refundPolicy: a.refundPolicy,
          collectAttendeeData: a.collectAttendeeData,
          tags: a.tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 10),
          ticketTypes: s.tickets.map((t) => ({
            name: t.name.trim(),
            price: t.isFree ? 0 : parseFloat(t.price) || 0,
            total: parseInt(t.total) || 0,
          })),
          tenantId: s.tenantId || (eligibleTenants.length === 1 ? eligibleTenants[0].tenant_id : undefined),
        },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      if (storageKey) localStorage.removeItem(storageKey);
      setCreated({ id: result.eventId, published: publish });
    } catch (err: any) {
      setSubmitError(err.message || "No pudimos guardar el evento. Probá de nuevo.");
    } finally {
      setSubmitting(null);
    }
  }

  if (!authLoading && !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 text-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Iniciá sesión</h2>
          <p className="mt-2 text-muted-foreground">Necesitás una cuenta para crear eventos.</p>
          <Link to="/login" className="mt-4 inline-block rounded-xl gradient-primary px-5 py-3 text-sm font-semibold text-primary-foreground">Ingresar</Link>
        </div>
      </div>
    );
  }

  if (created) return <SuccessScreen id={created.id} published={created.published} />;

  const prices = s.tickets.map((t) => (t.isFree ? 0 : parseFloat(t.price))).filter((n) => !isNaN(n));
  const fromPrice = prices.length ? Math.min(...prices) : null;

  return (
    <div className="mx-auto max-w-xl px-4 pb-32 pt-6">
      <Link to="/dashboard" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>

      {/* Barra de progreso siempre visible */}
      <div className="sticky top-0 z-10 -mx-4 bg-background/90 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
          <span>Paso {s.step} de 3 · {["Lo básico", "Entradas", "Publicar"][s.step - 1]}</span>
          {hydrated && <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Borrador guardado</span>}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={s.step}>
          <div className="h-full gradient-primary transition-all" style={{ width: `${(s.step / 3) * 100}%` }} />
        </div>
      </div>

      <div className="mt-4 space-y-5">
        {s.step === 1 && <Step1 s={s} set={set} errors={errors} />}
        {s.step === 2 && <Step2 s={s} setTicket={setTicket} setS={setS} errors={errors} />}
        {s.step === 3 && (
          <>
            <h1 className="text-2xl font-black text-foreground">Así lo verán tus asistentes</h1>
            <EventPreviewCard
              title={s.title} date={s.date} startTime={s.startTime} imageUrl={s.imageUrl}
              isVirtual={s.isVirtual} location={s.location} city={s.city} fromPrice={fromPrice}
            />

            {/* Indicador discreto de sala de espera */}
            {s.advanced.waitingRoomEnabled && (
              <button
                type="button" onClick={() => setAdvancedOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Sala de espera activa
              </button>
            )}

            {eligibleTenants.length > 1 && (
              <Field label="Organización" htmlFor="tenant">
                <select id="tenant" value={s.tenantId} onChange={(e) => set("tenantId", e.target.value)} className={inputCls}>
                  <option value="">Seleccioná una organización…</option>
                  {eligibleTenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{(t as any).tenant_name ?? t.tenant_id}</option>)}
                </select>
              </Field>
            )}

            <AdvancedOptions s={s} setAdv={setAdv} open={advancedOpen} setOpen={setAdvancedOpen} capacity={capacity} />

            {submitError && <div role="alert" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">{submitError}</div>}

            <div className="space-y-2.5 pt-2">
              <button
                type="button" disabled={!!submitting} onClick={() => submit(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl gradient-primary py-4 text-base font-bold text-primary-foreground glow-primary disabled:opacity-50"
              >
                {submitting === "publish" ? <><Loader2 className="h-5 w-5 animate-spin" /> Publicando…</> : "Publicar evento"}
              </button>
              <button
                type="button" disabled={!!submitting} onClick={() => submit(false)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-secondary py-3.5 text-sm font-semibold text-secondary-foreground disabled:opacity-50"
              >
                {submitting === "draft" ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</> : "Guardar como borrador"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Navegación del wizard: un solo foco de acción */}
      {s.step < 3 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-xl gap-3">
            {s.step > 1 && (
              <button type="button" onClick={goBack} className="rounded-xl border border-border bg-secondary px-5 py-4 text-sm font-semibold text-secondary-foreground">
                Atrás
              </button>
            )}
            <button type="button" onClick={goNext} className="flex flex-1 items-center justify-center gap-2 rounded-xl gradient-primary py-4 text-base font-bold text-primary-foreground">
              Continuar <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      {s.step === 3 && (
        <button type="button" onClick={goBack} className="mt-4 text-sm text-muted-foreground hover:text-foreground">← Volver a entradas</button>
      )}
    </div>
  );
}

/* ---------------- Paso 1 ---------------- */
function Step1({ s, set, errors }: { s: WizardState; set: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void; errors: StepErrors }) {
  return (
    <>
      <h1 className="text-2xl font-black text-foreground">Lo básico</h1>
      <Field label="Nombre del evento" htmlFor="title" error={errors.title}>
        <input id="title" autoFocus value={s.title} onChange={(e) => set("title", e.target.value)} maxLength={200} placeholder="Ej: Noche de Jazz en el Teatro" className={inputCls} />
      </Field>

      <div className="space-y-3">
        <Field label="Fecha" htmlFor="date" error={errors.date}>
          <input id="date" type="date" value={s.date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => set("date", e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Empieza" htmlFor="start">
            <input id="start" type="time" value={s.startTime} onChange={(e) => set("startTime", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Termina" htmlFor="end" error={errors.endTime}>
            <input id="end" type="time" value={s.endTime} onChange={(e) => set("endTime", e.target.value)} className={inputCls} />
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">Zona horaria: {s.timezone}</p>
      </div>

      <div>
        <span className="text-sm font-medium text-foreground">Lugar</span>
        <div className="mt-1 grid grid-cols-2 gap-1 rounded-xl bg-secondary p-1" role="radiogroup">
          {[{ v: false, l: "Presencial" }, { v: true, l: "Virtual" }].map((o) => (
            <button key={o.l} type="button" role="radio" aria-checked={s.isVirtual === o.v} onClick={() => set("isVirtual", o.v)}
              className={`rounded-lg py-2.5 text-sm font-semibold ${s.isVirtual === o.v ? "bg-background text-foreground shadow" : "text-muted-foreground"}`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="mt-3 space-y-3">
          {s.isVirtual ? (
            <Field label="Enlace del evento" htmlFor="vurl" error={errors.virtualUrl}>
              <input id="vurl" type="url" value={s.virtualUrl} onChange={(e) => set("virtualUrl", e.target.value)} placeholder="https://zoom.us/…" className={inputCls} />
            </Field>
          ) : (
            <>
              <Field label="Lugar" htmlFor="loc" error={errors.location}>
                <input id="loc" value={s.location} onChange={(e) => set("location", e.target.value)} maxLength={200} placeholder="Ej: Teatro Nacional" className={inputCls} />
              </Field>
              <Field label="Ciudad" htmlFor="city" error={errors.city}>
                <input id="city" value={s.city} onChange={(e) => set("city", e.target.value)} maxLength={100} placeholder="Ej: San José" className={inputCls} />
              </Field>
            </>
          )}
        </div>
      </div>

      <CoverUpload value={s.imageUrl} onChange={(v) => set("imageUrl", v)} />
    </>
  );
}

function CoverUpload({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { user } = useAuth();
  const ref = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function upload(file: File) {
    if (!user) return;
    if (!file.type.startsWith("image/")) { setErr("El archivo tiene que ser una imagen"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("La imagen no puede superar 5 MB"); return; }
    setErr(""); setBusy(true);
    try {
      const path = `${user.id}/${crypto.randomUUID()}.${file.name.split(".").pop() || "jpg"}`;
      const { error } = await supabase.storage.from("event-covers").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data, error: e2 } = await supabase.storage.from("event-covers").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      if (e2 || !data) throw e2 ?? new Error("No se pudo obtener la imagen");
      onChange(data.signedUrl);
    } catch (e: any) {
      setErr(e?.message || "No pudimos subir la imagen");
    } finally { setBusy(false); }
  }

  return (
    <div>
      <span className="text-sm font-medium text-foreground">Imagen de portada <span className="text-muted-foreground">(opcional)</span></span>
      {value ? (
        <div className="relative mt-1 overflow-hidden rounded-xl border border-border">
          <img src={value} alt="Portada" className="aspect-[16/9] w-full object-cover" />
          <button type="button" aria-label="Quitar imagen" onClick={() => onChange("")} className="absolute right-2 top-2 rounded-full bg-background/90 p-2">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button" onClick={() => ref.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) upload(f); }}
          className={`mt-1 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-8 text-sm ${drag ? "border-primary bg-primary/5" : "border-border bg-secondary/50"} text-muted-foreground`}
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
          {busy ? "Subiendo…" : "Arrastrá una imagen o tocá para elegir"}
          <span className="text-xs">Si la omitís, usamos una portada automática</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
      {err && <p role="alert" className="mt-1 text-xs font-medium text-destructive">{err}</p>}
    </div>
  );
}

/* ---------------- Paso 2 ---------------- */
function Step2({ s, setTicket, setS, errors }: {
  s: WizardState; setTicket: (id: string, p: Partial<WizardTicket>) => void;
  setS: React.Dispatch<React.SetStateAction<WizardState>>; errors: StepErrors;
}) {
  return (
    <>
      <h1 className="text-2xl font-black text-foreground">Entradas</h1>
      <div className="space-y-3">
        {s.tickets.map((t, i) => (
          <div key={t.id} className="space-y-3 rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-muted-foreground">Entrada {i + 1}</span>
              {s.tickets.length > 1 && (
                <button type="button" aria-label="Eliminar entrada" onClick={() => setS((p) => ({ ...p, tickets: p.tickets.filter((x) => x.id !== t.id) }))}
                  className="rounded-lg p-2 text-destructive/70 hover:bg-destructive/10">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <Field label="Nombre" htmlFor={`n-${t.id}`} error={errors[`${t.id}.name`]}>
              <input id={`n-${t.id}`} value={t.name} onChange={(e) => setTicket(t.id, { name: e.target.value })} maxLength={100} className={inputCls} />
            </Field>
            <div className="flex items-center justify-between rounded-xl bg-secondary/60 px-4 py-3">
              <span className="text-sm text-foreground">Entrada gratuita</span>
              <Switch checked={t.isFree} onChange={(v) => setTicket(t.id, { isFree: v })} label="Entrada gratuita" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!t.isFree && (
                <Field label="Precio (₡)" htmlFor={`p-${t.id}`} error={errors[`${t.id}.price`]}>
                  <input id={`p-${t.id}`} type="number" inputMode="decimal" min={0} value={t.price} onChange={(e) => setTicket(t.id, { price: e.target.value })} placeholder="5000" className={inputCls} />
                </Field>
              )}
              <Field label="Cantidad" htmlFor={`q-${t.id}`} error={errors[`${t.id}.total`]}>
                <input id={`q-${t.id}`} type="number" inputMode="numeric" min={1} value={t.total} onChange={(e) => setTicket(t.id, { total: e.target.value })} className={inputCls} />
              </Field>
            </div>
            {t.isFree && <p className="text-xs text-muted-foreground">Sin pago y sin reventa.</p>}
          </div>
        ))}
      </div>
      {s.tickets.length < 10 && (
        <button type="button"
          onClick={() => setS((p) => ({ ...p, tickets: [...p.tickets, { id: crypto.randomUUID(), name: "", price: "", total: "50", isFree: false }] }))}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border py-3.5 text-sm font-semibold text-foreground hover:bg-secondary">
          <Plus className="h-4 w-4" /> Agregar tipo de entrada
        </button>
      )}
    </>
  );
}

/* ---------------- Opciones avanzadas ---------------- */
function AdvancedOptions({ s, setAdv, open, setOpen, capacity }: {
  s: WizardState; setAdv: <K extends keyof WizardAdvanced>(k: K, v: WizardAdvanced[K]) => void;
  open: boolean; setOpen: (v: boolean) => void; capacity: number;
}) {
  const a = s.advanced;
  const cats = categories.filter((c) => c !== "Todos");
  return (
    <section className="rounded-2xl border border-border bg-card">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-4 text-sm font-semibold text-card-foreground">
        Opciones avanzadas <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-border px-4 py-4">
          <SalesAdvisor s={s} setAdv={setAdv} />
          {/* Sala de espera */}
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><ShieldCheck className="h-4 w-4 text-primary" /> Sala de espera virtual</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Ordena a los compradores en una fila cuando hay mucha demanda, para que la venta no se caiga y sea justa.
                  Podés apagarla en eventos pequeños donde no esperás picos de compra.
                </p>
              </div>
              <Switch checked={a.waitingRoomEnabled} onChange={(v) => setAdv("waitingRoomEnabled", v)} label="Sala de espera virtual" />
            </div>
            {a.waitingRoomEnabled && (
              <Field label="Activar la fila a partir de (compradores a la vez)" htmlFor="wrt" hint={`Automático: ${autoQueueThreshold(capacity)}`}>
                <input id="wrt" type="number" min={10} value={a.waitingRoomThreshold} onChange={(e) => setAdv("waitingRoomThreshold", e.target.value)} placeholder={String(autoQueueThreshold(capacity))} className={inputCls} />
              </Field>
            )}
          </div>

          <Field label="Aforo máximo" htmlFor="cap" hint={`Por defecto: ${totalTickets(s.tickets)} (suma de entradas)`}>
            <input id="cap" type="number" min={1} value={a.capacity} onChange={(e) => setAdv("capacity", e.target.value)} placeholder={String(totalTickets(s.tickets))} className={inputCls} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Inicio de venta" htmlFor="ss" hint="Por defecto: al publicar">
              <input id="ss" type="datetime-local" value={a.salesStart} onChange={(e) => setAdv("salesStart", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Fin de venta" htmlFor="se" hint="Por defecto: al empezar el evento">
              <input id="se" type="datetime-local" value={a.salesEnd} onChange={(e) => setAdv("salesEnd", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Máximo de entradas por persona" htmlFor="mpu">
            <input id="mpu" type="number" min={1} max={20} value={a.maxPerUser} onChange={(e) => setAdv("maxPerUser", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Descripción" htmlFor="desc">
            <textarea id="desc" rows={4} maxLength={2000} value={a.description} onChange={(e) => setAdv("description", e.target.value)} className={`${inputCls} resize-none`} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Categoría" htmlFor="cat">
              <select id="cat" value={a.category} onChange={(e) => setAdv("category", e.target.value)} className={inputCls}>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Etiquetas" htmlFor="tags" hint="Separadas por coma">
              <input id="tags" value={a.tags} onChange={(e) => setAdv("tags", e.target.value)} placeholder="jazz, al aire libre" className={inputCls} />
            </Field>
          </div>
          <Field label="Política de reembolso" htmlFor="ref">
            <select id="ref" value={a.refundPolicy} onChange={(e) => setAdv("refundPolicy", e.target.value as WizardAdvanced["refundPolicy"])} className={inputCls}>
              <option value="no_refund">Sin reembolsos</option>
              <option value="until_7_days">Hasta 7 días antes</option>
              <option value="until_48h">Hasta 48 h antes</option>
              <option value="flexible">Flexible (hasta el inicio)</option>
            </select>
          </Field>
          <div className="flex items-center justify-between gap-3 rounded-xl bg-secondary/60 px-4 py-3">
            <span className="text-sm text-foreground">Pedir nombre y teléfono de cada asistente</span>
            <Switch checked={a.collectAttendeeData} onChange={(v) => setAdv("collectAttendeeData", v)} label="Recolectar datos del asistente" />
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------- Éxito ---------------- */
function SuccessScreen({ id, published }: { id: string; published: boolean }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? `${window.location.origin}/events/${id}` : "";
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-10 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-success/20"><Check className="h-8 w-8 text-success" /></div>
      <h2 className="text-2xl font-bold text-foreground">{published ? "🎉 Tu evento está publicado" : "Borrador guardado"}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{published ? "Compartí el enlace para empezar a vender." : "Podés publicarlo cuando quieras desde el Dashboard."}</p>
      {published && (
        <button type="button" onClick={() => { navigator.clipboard.writeText(url); setCopied(true); }}
          className="mt-5 flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-foreground">
          <span className="truncate">{url}</span>{copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </button>
      )}
      <div className="mt-5 w-full space-y-2.5">
        <Link to="/events/$eventId" params={{ eventId: id }} className="block rounded-xl gradient-primary py-3.5 text-sm font-semibold text-primary-foreground">Ver evento</Link>
        <Link to="/dashboard/events/$eventId/venue" params={{ eventId: id }} className="block rounded-xl border border-border bg-secondary py-3 text-sm font-medium text-secondary-foreground">Configurar zonas y asientos (opcional)</Link>
        <Link to="/dashboard" className="block py-2 text-sm text-muted-foreground">Volver al Dashboard</Link>
      </div>
    </div>
  );
}

// Re-export for potential reuse without importing route module.
export { Building2 as _unused };
